import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function CourseBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [weeks, setWeeks]   = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving]   = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [dayForm, setDayForm]     = useState(null);
  const [msg, setMsg] = useState('');

  // New course form
  const [newCourse, setNewCourse] = useState({ title: '', description: '' });

  const load = () => {
    if (!id) return;
    Promise.all([
      api.get(`/courses/${id}`),
      api.get(`/courses/${id}/structure`),
    ]).then(([cr, sr]) => {
      setCourse(cr.data.data);
      setWeeks(sr.data.data.weeks);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const createCourse = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/courses', newCourse);
      navigate(`/course-builder/${r.data.data.id}`);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const addWeek = async () => {
    await api.post(`/courses/${id}/weeks`, {});
    load();
  };

  const openDay = (day) => {
    setActiveDay(day.id);
    setDayForm({
      title: day.title || '',
      text_content: day.text_content || '',
      youtube_url: day.youtube_url || '',
      youtube_title: day.youtube_title || '',
      quiz: day.quiz ? {
        passing_score: day.quiz.passing_score,
        questions: day.quiz.questions || [],
      } : null,
      project: day.project ? {
        title: day.project.title,
        description: day.project.description || '',
        steps: day.project.steps?.map(s => s.step_text) || [],
      } : null,
    });
    setMsg('');
  };

  const saveDay = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.put(`/days/${activeDay}`, dayForm);
      setMsg('Saved!');
      load();
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!id) {
    return (
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="content-section">
            <h5 className="fw-bold mb-4">Create New Course</h5>
            {msg && <div className="alert alert-danger py-2 small">{msg}</div>}
            <form onSubmit={createCourse}>
              <div className="mb-3">
                <label className="form-label small fw-semibold">Course Title *</label>
                <input className="form-control" value={newCourse.title} onChange={e=>setNewCourse({...newCourse,title:e.target.value})} required placeholder="e.g. React Training" />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-semibold">Description</label>
                <textarea className="form-control" rows={3} value={newCourse.description} onChange={e=>setNewCourse({...newCourse,description:e.target.value})} placeholder="Brief description..." />
              </div>
              <p className="text-muted small">8 weeks × 5 days will be created automatically.</p>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creating...' : 'Create Course'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;

  return (
    <div className="row g-3">
      {/* Left: week/day tree */}
      <div className="col-md-4 col-lg-3">
        <div className="content-section p-0" style={{maxHeight:'80vh',overflowY:'auto'}}>
          <div className="p-3 border-bottom">
            <h6 className="mb-0 fw-bold">{course?.title}</h6>
            <small className="text-muted">{weeks.length} weeks</small>
          </div>
          {weeks.map(week => (
            <div key={week.id}>
              <div className="px-3 py-2 bg-light border-bottom">
                <small className="fw-bold text-primary">Week {week.week_no}</small>
              </div>
              {week.days.map(day => (
                <div
                  key={day.id}
                  className={`px-3 py-2 border-bottom cursor-pointer ${activeDay===day.id ? 'bg-primary-subtle' : ''}`}
                  style={{cursor:'pointer'}}
                  onClick={() => openDay(day)}
                >
                  <div className="small fw-semibold">{day.title || `Day ${day.day_no}`}</div>
                  <div style={{fontSize:'.7rem'}} className="text-muted">
                    {day.text_content ? '📄' : ''} {day.youtube_url ? '🎬' : ''} {day.quiz_id ? '❓' : ''} {day.project_id ? '🔨' : ''}
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="p-3">
            <button className="btn btn-outline-primary btn-sm w-100" onClick={addWeek}>
              <i className="bi bi-plus-lg me-1" />Add Week
            </button>
          </div>
        </div>
      </div>

      {/* Right: day editor */}
      <div className="col-md-8 col-lg-9">
        {!dayForm ? (
          <div className="content-section text-center text-muted py-5">
            <i className="bi bi-arrow-left-circle fs-1 d-block mb-3 opacity-25" />
            <p>Select a day from the left panel to edit content</p>
          </div>
        ) : (
          <div>
            {msg && <div className={`alert py-2 small mb-3 ${msg==='Saved!' ? 'alert-success' : 'alert-danger'}`}>{msg}</div>}

            {/* Basic info */}
            <div className="content-section mb-3">
              <h6>Basic Info</h6>
              <div className="row g-2">
                <div className="col-sm-6">
                  <label className="form-label small">Day Title</label>
                  <input className="form-control form-control-sm" value={dayForm.title} onChange={e=>setDayForm({...dayForm,title:e.target.value})} />
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="content-section mb-3">
              <h6><i className="bi bi-file-text me-2 text-primary" />Text Content</h6>
              <textarea className="form-control form-control-sm" rows={6} value={dayForm.text_content} onChange={e=>setDayForm({...dayForm,text_content:e.target.value})} placeholder="Enter learning content..." />
            </div>

            {/* YouTube */}
            <div className="content-section mb-3">
              <h6><i className="bi bi-youtube me-2 text-danger" />YouTube Video</h6>
              <div className="row g-2">
                <div className="col-sm-6">
                  <label className="form-label small">Video Title</label>
                  <input className="form-control form-control-sm" value={dayForm.youtube_title} onChange={e=>setDayForm({...dayForm,youtube_title:e.target.value})} placeholder="e.g. Introduction to Components" />
                </div>
                <div className="col-sm-6">
                  <label className="form-label small">YouTube URL</label>
                  <input className="form-control form-control-sm" value={dayForm.youtube_url} onChange={e=>setDayForm({...dayForm,youtube_url:e.target.value})} placeholder="https://youtube.com/watch?v=..." />
                </div>
              </div>
            </div>

            {/* Quiz */}
            <div className="content-section mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0"><i className="bi bi-patch-question me-2 text-warning" />Quiz</h6>
                {!dayForm.quiz
                  ? <button className="btn btn-outline-warning btn-sm" onClick={()=>setDayForm({...dayForm,quiz:{passing_score:60,questions:[]}})}>Add Quiz</button>
                  : <button className="btn btn-outline-danger btn-sm" onClick={()=>setDayForm({...dayForm,quiz:null})}>Remove Quiz</button>
                }
              </div>
              {dayForm.quiz && (
                <>
                  <div className="mb-3">
                    <label className="form-label small">Passing Score (%)</label>
                    <input type="number" className="form-control form-control-sm" style={{maxWidth:100}} value={dayForm.quiz.passing_score} min={1} max={100}
                      onChange={e=>setDayForm({...dayForm,quiz:{...dayForm.quiz,passing_score:+e.target.value}})} />
                  </div>
                  {dayForm.quiz.questions.map((q, i) => (
                    <div key={i} className="border rounded p-3 mb-2 bg-light">
                      <div className="d-flex justify-content-between mb-2">
                        <small className="fw-bold">Question {i+1}</small>
                        <button className="btn btn-outline-danger btn-sm py-0 px-1" onClick={()=>{
                          const qs=[...dayForm.quiz.questions]; qs.splice(i,1);
                          setDayForm({...dayForm,quiz:{...dayForm.quiz,questions:qs}});
                        }}><i className="bi bi-trash" /></button>
                      </div>
                      <input className="form-control form-control-sm mb-2" placeholder="Question text" value={q.question||''}
                        onChange={e=>{const qs=[...dayForm.quiz.questions];qs[i]={...qs[i],question:e.target.value};setDayForm({...dayForm,quiz:{...dayForm.quiz,questions:qs}});}} />
                      {['option1','option2','option3','option4'].map((opt,oi) => (
                        <div key={opt} className="d-flex align-items-center gap-2 mb-1">
                          <span className="badge bg-secondary" style={{minWidth:20}}>{oi+1}</span>
                          <input className="form-control form-control-sm" placeholder={`Option ${oi+1}`} value={q[opt]||''}
                            onChange={e=>{const qs=[...dayForm.quiz.questions];qs[i]={...qs[i],[opt]:e.target.value};setDayForm({...dayForm,quiz:{...dayForm.quiz,questions:qs}});}} />
                          <input type="radio" name={`correct_${i}`} checked={q.correct_answer===q[opt]} title="Mark as correct"
                            onChange={()=>{const qs=[...dayForm.quiz.questions];qs[i]={...qs[i],correct_answer:qs[i][opt]};setDayForm({...dayForm,quiz:{...dayForm.quiz,questions:qs}});}} />
                        </div>
                      ))}
                      <small className="text-muted">Select radio button to mark correct answer</small>
                    </div>
                  ))}
                  <button className="btn btn-outline-primary btn-sm" onClick={()=>{
                    setDayForm({...dayForm,quiz:{...dayForm.quiz,questions:[...dayForm.quiz.questions,{question:'',option1:'',option2:'',option3:'',option4:'',correct_answer:''}]}});
                  }}><i className="bi bi-plus-lg me-1" />Add Question</button>
                </>
              )}
            </div>

            {/* Project */}
            <div className="content-section mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0"><i className="bi bi-kanban me-2" style={{color:'#6f42c1'}} />Project</h6>
                {!dayForm.project
                  ? <button className="btn btn-outline-secondary btn-sm" onClick={()=>setDayForm({...dayForm,project:{title:'',description:'',steps:[]}})}>Add Project</button>
                  : <button className="btn btn-outline-danger btn-sm" onClick={()=>setDayForm({...dayForm,project:null})}>Remove Project</button>
                }
              </div>
              {dayForm.project && (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-sm-6">
                      <label className="form-label small">Project Title</label>
                      <input className="form-control form-control-sm" value={dayForm.project.title} onChange={e=>setDayForm({...dayForm,project:{...dayForm.project,title:e.target.value}})} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small">Description</label>
                      <input className="form-control form-control-sm" value={dayForm.project.description} onChange={e=>setDayForm({...dayForm,project:{...dayForm.project,description:e.target.value}})} />
                    </div>
                  </div>
                  {dayForm.project.steps.map((step, i) => (
                    <div key={i} className="d-flex gap-2 mb-2">
                      <input className="form-control form-control-sm" value={step} placeholder={`Step ${i+1}`}
                        onChange={e=>{const s=[...dayForm.project.steps];s[i]=e.target.value;setDayForm({...dayForm,project:{...dayForm.project,steps:s}});}} />
                      <button className="btn btn-outline-danger btn-sm" onClick={()=>{const s=[...dayForm.project.steps];s.splice(i,1);setDayForm({...dayForm,project:{...dayForm.project,steps:s}});}}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  ))}
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>setDayForm({...dayForm,project:{...dayForm.project,steps:[...dayForm.project.steps,'']}})}>
                    <i className="bi bi-plus-lg me-1" />Add Step
                  </button>
                </>
              )}
            </div>

            <button className="btn btn-success" onClick={saveDay} disabled={saving}>
              {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />Save Day</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
